import { useQuery } from '@tanstack/react-query';
import {
  fetchInventoryKpis,
  fetchInventoryPerformance,
  fetchInventoryLocationValues,
  fetchInventoryLowStock,
  fetchInventoryRecentActivity,
} from '@/renderer/services/inventoryDashboardService';

export function useInventoryKpis(
  companyId: number | null,
  outletId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['inventory', 'dashboard', 'kpis', companyId, outletId],
    queryFn: async () => {
      const result = await fetchInventoryKpis(companyId!, outletId);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to load inventory KPIs');
      }
      return result.data;
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useInventoryPerformance(
  companyId: number | null,
  outletId: string | null | undefined,
  dateFrom: string,
  dateTo: string,
) {
  return useQuery({
    queryKey: [
      'inventory',
      'dashboard',
      'performance',
      companyId,
      outletId,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const result = await fetchInventoryPerformance(
        companyId!,
        outletId,
        dateFrom,
        dateTo,
      );
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to load performance charts');
      }
      return result.data;
    },
    enabled: !!companyId && !!dateFrom && !!dateTo,
    staleTime: 1000 * 60 * 5,
  });
}

export function useInventoryLocationValues(
  companyId: number | null,
  outletId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['inventory', 'dashboard', 'locationValues', companyId, outletId],
    queryFn: async () => {
      const result = await fetchInventoryLocationValues(companyId!, outletId);
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to load location values');
      }
      return result.data;
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useInventoryLowStock(
  companyId: number | null,
  outletId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['inventory', 'dashboard', 'lowStock', companyId, outletId],
    queryFn: async () => {
      const result = await fetchInventoryLowStock(companyId!, outletId);
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to load low stock list');
      }
      return result.data;
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useInventoryRecentActivity(
  companyId: number | null,
  outletId: string | null | undefined,
  dateFrom: string,
  dateTo: string,
) {
  return useQuery({
    queryKey: [
      'inventory',
      'dashboard',
      'recentActivity',
      companyId,
      outletId,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const result = await fetchInventoryRecentActivity(
        companyId!,
        outletId,
        dateFrom,
        dateTo,
      );
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to load recent activity');
      }
      return result.data;
    },
    enabled: !!companyId && !!dateFrom && !!dateTo,
    staleTime: 1000 * 60 * 5,
  });
}
